import { supabase } from './supabase.js';
import { CONFIG } from '../config.js';

// Helper para consultar tablas con más de 1000 registros paginando en bloques de 1000
async function fetchAllRows(table, selectQuery, orderCol = 'id') {
  let all = [];
  let from = 0;
  const step = 1000;
  while (true) {
    let query = supabase.from(table).select(selectQuery);
    if (orderCol) {
      query = query.order(orderCol, { ascending: true });
    }
    const { data, error } = await query.range(from, from + step - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all = all.concat(data);
    if (data.length < step) break;
    from += step;
  }
  return all;
}

export const DataService = {
  // Database Health Check
  async pingDatabase() {
    try {
      const start = performance.now();
      const { data, error } = await supabase.from('rates').select('count', { count: 'exact', head: true });
      const duration = Math.round(performance.now() - start);
      if (error) return { ok: false, ping: duration, error: error.message };
      return { ok: true, ping: duration };
    } catch (e) {
      return { ok: false, ping: 0, error: e.message };
    }
  },

  // Rates (ABM de Tarifas)
  async getRates() {
    const { data, error } = await supabase
      .from('rates')
      .select('*')
      .order('id', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async createRate(rate) {
    const { data, error } = await supabase
      .from('rates')
      .insert([rate])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateRate(id, updates) {
    const { data, error } = await supabase
      .from('rates')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Clients
  async getClients() {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        contacts (*),
        projects (id, name, status, budget_amount)
      `)
      .order('id', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async createClient(client) {
    const { data, error } = await supabase
      .from('clients')
      .insert([client])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateClient(id, updates) {
    const { data, error } = await supabase
      .from('clients')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteClient(id) {
    const clientId = Number(id);
    // 1. Eliminar diagnósticos vinculados
    await supabase.from('lead_diagnostics').delete().eq('client_id', clientId);
    // 2. Eliminar oportunidades
    await supabase.from('opportunities').delete().eq('client_id', clientId);
    // 3. Eliminar actividades
    await supabase.from('activities').delete().eq('entity_type', 'client').eq('entity_id', clientId);
    // 4. Eliminar contactos
    await supabase.from('contacts').delete().eq('client_id', clientId);
    // 5. Eliminar logs de emails
    await supabase.from('email_logs').delete().eq('client_id', clientId);
    // 6. Eliminar cliente
    const { data, error } = await supabase
      .from('clients')
      .delete()
      .eq('id', clientId);
    if (error) throw error;
    return data;
  },

  // Opportunities / Commercial Pipeline
  async getOpportunities() {
    const { data, error } = await supabase
      .from('opportunities')
      .select('*, client:clients(name, country, channel, company)')
      .order('id', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async updateOpportunityStage(id, stage) {
    const { data, error } = await supabase
      .from('opportunities')
      .update({ stage, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async createOpportunity(opportunity) {
    const { data, error } = await supabase
      .from('opportunities')
      .insert([opportunity])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Projects
  async getProjects() {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        client:clients(id, name, channel, country),
        rate:rates(id, name, hourly_rate, currency, commission_percent),
        tasks(
          id, title, priority, status, estimated_hours, estimated_cost, start_date, due_date,
          subtasks(id, date, author, actual_hours, description, hourly_rate_applied)
        )
      `)
      .order('id', { ascending: false });
    if (error) throw error;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Calculate aggregated actual hours and costs for each project
    return (data || []).map(p => {
      let totalActualHours = 0;
      let totalEstimatedHours = 0;
      let tasksCount = p.tasks?.length || 0;
      let completedTasks = 0;
      let tmAccruedTotal = 0;
      let tmAccruedCurrentMonth = 0;

      const hourlyRate = Number(p.rate?.hourly_rate || 0);
      const discountPercent = Number(p.discount_percent || 0);
      const effectiveRate = discountPercent > 0 ? hourlyRate * (1 - discountPercent / 100) : hourlyRate;

      const enrichedTasks = (p.tasks || []).map(t => {
        let taskHours = 0;
        let taskAccrued = 0;
        totalEstimatedHours += Number(t.estimated_hours || 0);
        if (t.status === 'Completada') completedTasks++;

        const subtasks = (t.subtasks || []).map(st => {
          const hrs = Number(st.actual_hours || 0);
          taskHours += hrs;
          totalActualHours += hrs;
          const stRate = (st.hourly_rate_applied !== null && st.hourly_rate_applied !== undefined)
            ? Number(st.hourly_rate_applied)
            : effectiveRate;
          const stAccrued = Math.round(hrs * stRate);
          taskAccrued += stAccrued;
          tmAccruedTotal += (hrs * stRate);

          if (st.date) {
            const parts = st.date.split('-');
            if (parts.length >= 2) {
              const stY = parseInt(parts[0], 10);
              const stM = parseInt(parts[1], 10) - 1;
              if (stY === currentYear && stM === currentMonth) {
                tmAccruedCurrentMonth += (hrs * stRate);
              }
            }
          }

          return {
            ...st,
            hourly_rate_applied: stRate,
            accrued_amount: stAccrued
          };
        });

        return {
          ...t,
          subtasks,
          actual_hours: taskHours,
          accrued_amount: taskAccrued,
          deviation_percent: t.estimated_hours > 0 
            ? (((taskHours - t.estimated_hours) / t.estimated_hours) * 100).toFixed(1)
            : '0.0'
        };
      });

      const budgetHours = Number(p.budget_hours || 0);
      const budgetAmount = Number(p.budget_amount || 0);

      // Financial calculations:
      const rawTmAmount = totalActualHours * hourlyRate;
      const billableAmount = p.billing_mode === 'time_and_materials' 
        ? (totalActualHours > 0 ? tmAccruedTotal : (discountPercent > 0 ? rawTmAmount * (1 - discountPercent / 100) : rawTmAmount))
        : budgetAmount;

      // Current month devengado
      let currentMonthDevengado = 0;
      const isInternal = p.client_id === 12 || (p.name || '').toLowerCase().includes('alerce');
      if (!isInternal) {
        if (p.revenue_type === 'recurrente') {
          const pStart = p.start_date ? p.start_date.split('-') : null;
          const pEnd = p.end_date ? p.end_date.split('-') : null;
          const hasStarted = !pStart || (parseInt(pStart[0], 10) < currentYear || (parseInt(pStart[0], 10) === currentYear && parseInt(pStart[1], 10) - 1 <= currentMonth));
          const hasEnded = pEnd && (p.status === 'Completado' || p.status === 'Cancelado') &&
            (parseInt(pEnd[0], 10) < currentYear || (parseInt(pEnd[0], 10) === currentYear && parseInt(pEnd[1], 10) - 1 < currentMonth));
          if (hasStarted && !hasEnded) {
            currentMonthDevengado = budgetAmount;
          }
        } else if (p.billing_mode === 'time_and_materials') {
          currentMonthDevengado = tmAccruedCurrentMonth;
        } else {
          const pStart = p.start_date ? p.start_date.split('-') : null;
          if (pStart && parseInt(pStart[0], 10) === currentYear && parseInt(pStart[1], 10) - 1 === currentMonth) {
            currentMonthDevengado = budgetAmount;
          }
        }
      }

      // Budget consumption for fixed budget:
      const budgetConsumptionPercent = budgetHours > 0 ? (totalActualHours / budgetHours) * 100 : 0;
      const isOverBudget = p.billing_mode !== 'time_and_materials' && budgetHours > 0 && totalActualHours > budgetHours;
      const overBudgetHours = isOverBudget ? totalActualHours - budgetHours : 0;

      return {
        ...p,
        tasks: enrichedTasks,
        discount_percent: discountPercent,
        validity_days: p.validity_days || 15,
        total_estimated_hours: totalEstimatedHours,
        total_actual_hours: totalActualHours,
        tasks_count: tasksCount,
        completed_tasks_count: completedTasks,
        remaining_hours: (p.budget_hours || totalEstimatedHours) - totalActualHours,
        billable_amount: billableAmount,
        raw_tm_amount: rawTmAmount,
        current_month_devengado: Math.round(currentMonthDevengado),
        budget_consumption_percent: budgetConsumptionPercent,
        is_over_budget: isOverBudget,
        over_budget_hours: overBudgetHours
      };
    });
  },

  async createProject(project) {
    const { data, error } = await supabase
      .from('projects')
      .insert([project])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateProject(id, updates) {
    const { data, error } = await supabase
      .from('projects')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Tasks & Subtasks
  async getTasks(projectId) {
    let query = supabase
      .from('tasks')
      .select(`
        *,
        project:projects(id, name, billing_mode, discount_percent, currency, rate:rates(id, hourly_rate, currency)),
        subtasks(id, date, author, actual_hours, description, hourly_rate_applied)
      `)
      .order('id', { ascending: true });

    if (projectId) {
      query = query.eq('project_id', projectId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map(t => {
      const proj = t.project;
      const rate = Number(proj?.rate?.hourly_rate || 0);
      const disc = Number(proj?.discount_percent || 0);
      const effectiveRate = disc > 0 ? rate * (1 - disc / 100) : rate;

      let taskActualHours = 0;
      let taskAccruedAmount = 0;

      const subtasks = (t.subtasks || []).map(st => {
        const hrs = Number(st.actual_hours || 0);
        taskActualHours += hrs;
        const subRate = (st.hourly_rate_applied !== null && st.hourly_rate_applied !== undefined)
          ? Number(st.hourly_rate_applied)
          : effectiveRate;
        const accrued = Math.round(hrs * subRate);
        taskAccruedAmount += accrued;
        return {
          ...st,
          hourly_rate_applied: subRate,
          accrued_amount: accrued
        };
      });

      return {
        ...t,
        subtasks,
        actual_hours: taskActualHours,
        accrued_amount: taskAccruedAmount,
        deviation_percent: t.estimated_hours > 0 
          ? (((taskActualHours - t.estimated_hours) / t.estimated_hours) * 100).toFixed(1)
          : '0.0'
      };
    });
  },

  async createTask(task) {
    const { data, error } = await supabase
      .from('tasks')
      .insert([task])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateTask(id, updates) {
    const { data, error } = await supabase
      .from('tasks')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Subtasks (Numerical hour entry per activity)
  async createSubtask(subtask) {
    let rateApplied = subtask.hourly_rate_applied;
    if (rateApplied === undefined || rateApplied === null) {
      try {
        const { data: taskData } = await supabase
          .from('tasks')
          .select('project:projects(id, client_id, discount_percent, rate:rates(hourly_rate))')
          .eq('id', subtask.task_id)
          .single();
        const proj = taskData?.project;
        if (proj && proj.client_id !== 12) {
          const baseRate = Number(proj.rate?.hourly_rate || 0);
          const disc = Number(proj.discount_percent || 0);
          rateApplied = Math.round(disc > 0 ? baseRate * (1 - disc / 100) : baseRate);
        } else {
          rateApplied = 0;
        }
      } catch {
        rateApplied = 0;
      }
    }

    const { data, error } = await supabase
      .from('subtasks')
      .insert([{ ...subtask, hourly_rate_applied: rateApplied }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateSubtask(id, updates) {
    const { data, error } = await supabase
      .from('subtasks')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteSubtask(id) {
    const { error } = await supabase
      .from('subtasks')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  // Invoices & Payments
  async getInvoices() {
    const data = await fetchAllRows(
      'invoices',
      '*, client:clients(name), project:projects(id, name, billing_mode, revenue_type, budget_amount, start_date, rate:rates(id, name, hourly_rate, currency))',
      'id'
    );
    return data || [];
  },

  async getPayments() {
    const data = await fetchAllRows(
      'payments',
      '*, client:clients(name), project:projects(id, name, billing_mode, revenue_type)',
      'id'
    );
    return data || [];
  },

  async createPayment(payment) {
    const { data, error } = await supabase
      .from('payments')
      .insert([payment])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updatePayment(id, updates) {
    const { data, error } = await supabase
      .from('payments')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deletePayment(id) {
    const { error } = await supabase
      .from('payments')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  },

  // Resumen Financiero Completo: Devengado vs Facturado Fiscal vs Cobrado
  async getFinancialSummary(filter = {}) {
    const { clientId, dateFrom, dateTo } = filter;
    const targetClientId = clientId && clientId !== 'all' ? Number(clientId) : null;

    const [projects, invoices, payments, subtasks] = await Promise.all([
      this.getProjects(),
      this.getInvoices(),
      this.getPayments(),
      fetchAllRows(
        'subtasks',
        'id, date, actual_hours, hourly_rate_applied, task:tasks(id, project_id, project:projects(id, client_id, billing_mode, revenue_type, discount_percent, rate:rates(id, hourly_rate)))'
      )
    ]);

    const isWithinDateRange = (dateStr) => {
      if (!dateStr) return false;
      if (dateFrom && dateStr < dateFrom) return false;
      if (dateTo && dateStr > dateTo) return false;
      return true;
    };

    const hasDateFilter = Boolean(dateFrom || dateTo);

    const round2 = (num) => Math.round((Number(num) || 0) * 100) / 100;

    // 1. Filtrar Invoices (excluyendo registros internos)
    const filteredInvoices = invoices.filter(inv => {
      const isInternal = inv.client_id === 12 || (inv.project?.name || '').toLowerCase().includes('alerce');
      if (isInternal) return false;
      const invClientId = inv.client_id || inv.project?.client_id;
      if (targetClientId && invClientId !== targetClientId) return false;
      if (hasDateFilter && (!inv.issue_date || !isWithinDateRange(inv.issue_date))) return false;
      return true;
    });

    // 2. Filtrar Payments (por fecha de imputación; si está vacía, fallback a fecha de cobro; excluyendo internos)
    const filteredPayments = payments.filter(pay => {
      const isInternal = pay.client_id === 12 || (pay.project?.name || '').toLowerCase().includes('alerce');
      if (isInternal) return false;
      const payClientId = pay.client_id || pay.project?.client_id;
      if (targetClientId && payClientId !== targetClientId) return false;
      const effectiveDate = pay.imputation_date || pay.payment_date;
      if (hasDateFilter && (!effectiveDate || !isWithinDateRange(effectiveDate))) return false;
      return true;
    });

    // 3. Devengado por Proyecto y Global
    const now = new Date();
    const currentY = now.getFullYear();
    const currentM = now.getMonth();

    const projectSubtasksMap = {};
    subtasks.forEach(st => {
      const proj = st.task?.project;
      if (!proj) return;
      if (!projectSubtasksMap[proj.id]) projectSubtasksMap[proj.id] = [];
      projectSubtasksMap[proj.id].push(st);
    });

    const filteredProjects = projects.filter(p => {
      if (targetClientId && p.client_id !== targetClientId) return false;
      return true;
    });

    const projectSummaries = [];

    filteredProjects.forEach(p => {
      const isInternal = p.client_id === 12 || (p.name || '').toLowerCase().includes('alerce');
      let projDevengado = 0;

      if (!isInternal) {
        if (p.revenue_type === 'recurrente') {
          if (p.start_date) {
            const parts = p.start_date.split('-');
            const startY = parseInt(parts[0], 10);
            const startM = parseInt(parts[1], 10) - 1;
            const endParts = p.end_date ? p.end_date.split('-') : null;
            const endY = endParts ? parseInt(endParts[0], 10) : currentY;
            const endM = endParts ? parseInt(endParts[1], 10) - 1 : currentM;

            if (hasDateFilter) {
              let monthsCount = 0;
              for (let y = startY; y <= Math.min(endY, currentY); y++) {
                const mStart = (y === startY) ? startM : 0;
                const mEnd = (y === endY || y === currentY) ? Math.min((y === endY ? endM : 11), (y === currentY ? currentM : 11)) : 11;
                for (let m = mStart; m <= mEnd; m++) {
                  const mStr = `${y}-${String(m + 1).padStart(2, '0')}-01`;
                  if ((!dateFrom || mStr >= dateFrom.substring(0, 7) + '-01') && 
                      (!dateTo || mStr <= dateTo.substring(0, 7) + '-28')) {
                    monthsCount++;
                  }
                }
              }
              projDevengado = monthsCount * Number(p.budget_amount || 0);
            } else {
              const targetEndY = Math.min(endY, currentY);
              const targetEndM = endY < currentY ? endM : Math.min(endM, currentM);
              const monthsCount = Math.max(0, (targetEndY - startY) * 12 + (targetEndM - startM) + 1);
              projDevengado = monthsCount * Number(p.budget_amount || 0);
            }
          } else {
            projDevengado = Number(p.budget_amount || 0);
          }
        } else if (p.billing_mode === 'time_and_materials') {
          const pSubtasks = projectSubtasksMap[p.id] || [];
          pSubtasks.forEach(st => {
            if (hasDateFilter && !isWithinDateRange(st.date)) return;
            const rate = Number(p.rate?.hourly_rate || 0);
            const disc = Number(p.discount_percent || 0);
            const effectiveRate = disc > 0 ? rate * (1 - disc / 100) : rate;
            const stRate = (st.hourly_rate_applied !== null && st.hourly_rate_applied !== undefined)
              ? Number(st.hourly_rate_applied)
              : effectiveRate;
            projDevengado += (Number(st.actual_hours || 0) * stRate);
          });
        } else {
          // Presupuesto Fijo (puntual)
          if (hasDateFilter) {
            const effectiveProjectDate = p.end_date || p.start_date;
            if (effectiveProjectDate && isWithinDateRange(effectiveProjectDate)) {
              projDevengado = Number(p.budget_amount || 0);
            } else {
              projDevengado = 0;
            }
          } else {
            projDevengado = Number(p.budget_amount || 0);
          }
        }
      }

      const projInvoices = filteredInvoices.filter(i => i.project_id === p.id);
      const projPayments = filteredPayments.filter(pay => pay.project_id === p.id);

      const projFacturado = round2(projInvoices.reduce((acc, i) => acc + Number(i.amount_billed || 0), 0));
      const projCobrado = round2(projPayments.reduce((acc, pay) => acc + Number(pay.amount_paid || 0), 0));
      const projDevengadoRounded = round2(projDevengado);
      const projPendienteCobro = round2(Math.max(0, projDevengadoRounded - projCobrado));

      projectSummaries.push({
        id: p.id,
        project_id: p.id,
        name: p.name,
        client: p.client,
        client_id: p.client_id,
        billing_mode: p.billing_mode,
        revenue_type: p.revenue_type || 'puntual',
        currency: p.currency || p.rate?.currency || 'ARS',
        devengado: projDevengadoRounded,
        cobrado: projCobrado,
        facturado: projFacturado,
        pendienteCobro: projPendienteCobro,
        isInternal
      });
    });

    // Totales calculados directamente desde los proyectos no internos para garantizar
    // coincidencia matemática exacta al 100% (hasta el último centavo) con la tabla y cobranzas
    const nonInternalProjects = projectSummaries.filter(p => !p.isInternal);
    const totalDevengado = round2(nonInternalProjects.reduce((acc, p) => acc + p.devengado, 0));
    const totalCobrado = round2(nonInternalProjects.reduce((acc, p) => acc + p.cobrado, 0));
    const totalFacturado = round2(nonInternalProjects.reduce((acc, p) => acc + p.facturado, 0));
    const devengadoPendienteCobro = round2(nonInternalProjects.reduce((acc, p) => acc + p.pendienteCobro, 0));

    return {
      totalDevengado,
      totalCobrado,
      totalFacturado,
      devengadoPendienteCobro,
      saldoFacturadoPorCobrar: round2(Math.max(0, totalFacturado - totalCobrado)),
      pendienteDeFacturar: round2(Math.max(0, totalDevengado - totalFacturado)),
      invoicesCount: filteredInvoices.length,
      paymentsCount: filteredPayments.length,
      projectSummaries,
      filteredInvoices,
      filteredPayments
    };
  },

  // Aggregated Dashboard KPI Metrics
  async getDashboardMetrics(filter = {}) {
    const now = new Date();
    const targetYear = filter.year !== undefined && filter.year !== null ? Number(filter.year) : now.getFullYear();
    const targetMonth = filter.month !== undefined && filter.month !== null ? Number(filter.month) : now.getMonth();
    const monthKey = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}`;

    const [projectsRes, clientsRes, oppsRes, subtasksRes, invoicesRes, paymentsRes, tasksRes, settingsRes] = await Promise.all([
      supabase.from('projects').select(`
        id, name, status, budget_amount, budget_hours, billing_mode, revenue_type, start_date, end_date, discount_percent, client_id,
        client:clients(id, name, channel),
        rate:rates(id, name, hourly_rate, currency),
        tasks(id, subtasks(id, actual_hours, date))
      `).order('id', { ascending: false }),
      supabase.from('clients').select('id, name, phase, channel'),
      supabase.from('opportunities').select('id, estimated_amount, stage'),
      fetchAllRows('subtasks', 'id, actual_hours, date, description, hourly_rate_applied, task:tasks(id, title, project_id, project:projects(id, name, billing_mode, revenue_type, discount_percent, client_id, rate:rates(id, name, hourly_rate, currency)))'),
      fetchAllRows('invoices', 'id, amount_project, amount_billed, amount_collected, balance_pending, issue_date, project:projects(id, name, billing_mode, revenue_type, start_date)'),
      fetchAllRows('payments', 'id, amount_paid, amount_billed, payment_date, project:projects(id, name, billing_mode, revenue_type)'),
      supabase.from('tasks').select('id, title, priority, status, due_date, project:projects(name)').order('due_date', { ascending: true }),
      supabase.from('settings').select('value').eq('key', 'monthly_parameters').single()
    ]);

    const projects = projectsRes.data || [];
    const clients = clientsRes.data || [];
    const opps = oppsRes.data || [];
    const subtasks = subtasksRes || [];
    const invoices = invoicesRes || [];
    const payments = paymentsRes || [];
    const tasks = tasksRes.data || [];
    const monthlyParameters = settingsRes.data?.value || {};

    const parseYMD = (dateStr) => {
      if (!dateStr || typeof dateStr !== 'string') return null;
      const parts = dateStr.trim().split('-');
      if (parts.length >= 2) {
        return {
          year: parseInt(parts[0], 10),
          month: parseInt(parts[1], 10) - 1,
          day: parseInt(parts[2] || '1', 10)
        };
      }
      return null;
    };

    const computeMonthMetrics = (y, m) => {
      let monthHours = 0;
      let monthFixedHours = 0;
      let monthTmHours = 0;
      let monthInternalHours = 0;
      let monthRecurringHours = 0;
      let monthPunctualHours = 0;
      let accruedTm = 0;
      const projectHoursMap = {};

      // 1. Horas y Devengado por Horas Reales (Time & Materials)
      subtasks.forEach(st => {
        const parsed = parseYMD(st.date);
        if (!parsed || parsed.year !== y || parsed.month !== m) return;

        const hrs = Number(st.actual_hours || 0);
        if (hrs <= 0) return;

        monthHours += hrs;
        const proj = st.task?.project;
        const isTm = proj?.billing_mode === 'time_and_materials';
        const isRecurring = proj?.revenue_type === 'recurrente';
        const isInternal = !proj || proj.client_id === 12 || (proj.name || '').toLowerCase().includes('alerce');

        if (isTm) monthTmHours += hrs;
        else monthFixedHours += hrs;

        if (isInternal) monthInternalHours += hrs;
        if (isRecurring) monthRecurringHours += hrs;
        else monthPunctualHours += hrs;

        if (proj) {
          const pId = proj.id;
          if (!projectHoursMap[pId]) {
            projectHoursMap[pId] = {
              id: pId,
              name: proj.name,
              hours: 0,
              isInternal,
              billingMode: proj.billing_mode,
              revenueType: proj.revenue_type || 'puntual'
            };
          }
          projectHoursMap[pId].hours += hrs;
        }

        if (isTm && !isInternal && proj) {
          const rate = Number(proj.rate?.hourly_rate || 0);
          const disc = Number(proj.discount_percent || 0);
          const effectiveRate = disc > 0 ? rate * (1 - disc / 100) : rate;
          const stRate = (st.hourly_rate_applied !== null && st.hourly_rate_applied !== undefined)
            ? Number(st.hourly_rate_applied)
            : effectiveRate;
          accruedTm += (hrs * stRate);
        }
      });

      // 2. Ingresos Recurrentes (abono mensual devengado en cada mes de vigencia del proyecto)
      let accruedRecurring = 0;
      projects.forEach(p => {
        if (p.revenue_type === 'recurrente') {
          const pStart = parseYMD(p.start_date);
          const pEnd = parseYMD(p.end_date);
          const hasStarted = !pStart || (pStart.year < y || (pStart.year === y && pStart.month <= m));
          const hasEnded = pEnd && (p.status === 'Completado' || p.status === 'Cancelado') && 
            (pEnd.year < y || (pEnd.year === y && pEnd.month < m));

          if (hasStarted && !hasEnded) {
            accruedRecurring += Number(p.budget_amount || 0);
          }
        }
      });

      // 3. Ingresos Puntuales de Presupuesto Fijo (devengados en el mes de inicio start_date)
      let accruedFixedPunctual = 0;
      projects.forEach(p => {
        if (p.billing_mode === 'fixed_budget' && p.revenue_type !== 'recurrente') {
          const pStart = parseYMD(p.start_date);
          if (pStart && pStart.year === y && pStart.month === m) {
            accruedFixedPunctual += Number(p.budget_amount || 0);
          }
        }
      });

      const totalDevengado = accruedTm + accruedRecurring + accruedFixedPunctual;
      const hourlyValue = monthHours > 0 ? Math.round(totalDevengado / monthHours) : 0;

      return {
        totalDevengado,
        accruedTm,
        accruedRecurring,
        accruedFixedPunctual,
        accruedByBudget: accruedRecurring + accruedFixedPunctual,
        accruedByHours: accruedTm,
        accruedRecurringTotal: accruedRecurring,
        accruedPunctualTotal: accruedTm + accruedFixedPunctual,
        monthHours,
        monthFixedHours,
        monthTmHours,
        monthInternalHours,
        monthRecurringHours,
        monthPunctualHours,
        hourlyValue,
        projectHoursMap
      };
    };

    const currentMetrics = computeMonthMetrics(targetYear, targetMonth);

    // Comparativa con mes anterior
    let prevYear = targetMonth === 0 ? targetYear - 1 : targetYear;
    let prevMonth = targetMonth === 0 ? 11 : targetMonth - 1;
    const prevMetrics = computeMonthMetrics(prevYear, prevMonth);
    const prevMonthKey = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}`;
    const prevInflationRate = Number(monthlyParameters[prevMonthKey]?.inflation_rate || 0);
    const prevHourlyValueInflationAdjusted = Math.round(prevMetrics.hourlyValue * (1 + prevInflationRate / 100));

    let hourlyValueChangeReal = 0;
    if (prevHourlyValueInflationAdjusted > 0) {
      hourlyValueChangeReal = Math.round(((currentMetrics.hourlyValue - prevHourlyValueInflationAdjusted) / prevHourlyValueInflationAdjusted) * 100);
    }
    let hourlyValueChangeNominal = 0;
    if (prevMetrics.hourlyValue > 0) {
      hourlyValueChangeNominal = Math.round(((currentMetrics.hourlyValue - prevMetrics.hourlyValue) / prevMetrics.hourlyValue) * 100);
    }

    // Punto de Equilibrio
    const fixedCost = Number(monthlyParameters[monthKey]?.fixed_cost || 0);
    const inflationRate = Number(monthlyParameters[monthKey]?.inflation_rate || 0);
    const coveredPercent = fixedCost > 0 ? Math.min(Math.round((currentMetrics.totalDevengado / fixedCost) * 100), 999) : 100;
    const isCovered = currentMetrics.totalDevengado >= fixedCost;

    let breakEvenDay = null;
    if (fixedCost > 0 && isCovered) {
      let cumulated = currentMetrics.accruedRecurring;
      if (cumulated >= fixedCost) {
        breakEvenDay = 1;
      } else {
        for (let d = 1; d <= 31; d++) {
          // 1. Sumar devengado de horas T&M en el día d
          subtasks.forEach(st => {
            const parsed = parseYMD(st.date);
            if (parsed && parsed.year === targetYear && parsed.month === targetMonth && parsed.day === d) {
              const proj = st.task?.project;
              const isTm = proj?.billing_mode === 'time_and_materials';
              const isInternal = !proj || proj.client_id === 12 || (proj.name || '').toLowerCase().includes('alerce');
              if (isTm && !isInternal && proj) {
                const rate = Number(proj.rate?.hourly_rate || 0);
                const disc = Number(proj.discount_percent || 0);
                const effRate = disc > 0 ? rate * (1 - disc / 100) : rate;
                const stRate = (st.hourly_rate_applied !== null && st.hourly_rate_applied !== undefined)
                  ? Number(st.hourly_rate_applied)
                  : effRate;
                cumulated += (Number(st.actual_hours || 0) * stRate);
              }
            }
          });

          // 2. Sumar proyectos puntuales de presupuesto fijo iniciados en el día d
          projects.forEach(p => {
            if (p.billing_mode === 'fixed_budget' && p.revenue_type !== 'recurrente') {
              const pStart = parseYMD(p.start_date);
              if (pStart && pStart.year === targetYear && pStart.month === targetMonth && pStart.day === d) {
                cumulated += Number(p.budget_amount || 0);
              }
            }
          });

          if (cumulated >= fixedCost && breakEvenDay === null) {
            breakEvenDay = d;
            break;
          }
        }
      }
    }
    const breakEvenDate = breakEvenDay ? `${String(breakEvenDay).padStart(2, '0')}/${String(targetMonth + 1).padStart(2, '0')}` : null;

    // Horas de los últimos 7 días
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    let currentWeekHours = 0;
    subtasks.forEach(st => {
      const parsed = parseYMD(st.date);
      if (parsed) {
        const d = new Date(parsed.year, parsed.month, parsed.day);
        if (d >= sevenDaysAgo && d <= now) {
          currentWeekHours += Number(st.actual_hours || 0);
        }
      }
    });

    // Serie histórica de evolución (6 meses)
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const evolutionHistory = [];
    for (let i = 5; i >= 0; i--) {
      let m = targetMonth - i;
      let y = targetYear;
      while (m < 0) {
        m += 12;
        y -= 1;
      }
      const mMetrics = computeMonthMetrics(y, m);
      const mKey = `${y}-${String(m + 1).padStart(2, '0')}`;
      const mInf = Number(monthlyParameters[mKey]?.inflation_rate || 0);

      evolutionHistory.push({
        key: mKey,
        year: y,
        month: m,
        label: `${monthNames[m]} ${String(y).slice(2)}`,
        ingresos: Math.round(mMetrics.totalDevengado),
        horas: Math.round(mMetrics.monthHours * 10) / 10,
        valorHoraNominal: Math.round(mMetrics.hourlyValue),
        valorHoraReal: Math.round(mMetrics.hourlyValue * (1 + mInf / 100))
      });
    }

    // Situación Comercial (4 etapas del pipeline)
    const pipelinePotenciales = clients.filter(c => c.phase === 'Cliente potencial').length;
    const pipelinePropuestas = clients.filter(c => c.phase === 'Propuesta en curso').length;
    const pipelineGanados = clients.filter(c => c.phase === 'Cerrado' || c.phase === 'Ganado / activo').length;
    const pipelinePausados = clients.filter(c => c.phase === 'Descartado' || c.phase === 'En pausa').length;

    // Horas por proyecto desglosadas
    const hoursByProject = Object.values(currentMetrics.projectHoursMap)
      .sort((a, b) => b.hours - a.hours)
      .map(p => ({
        ...p,
        percent: currentMetrics.monthHours > 0 ? Math.round((p.hours / currentMetrics.monthHours) * 100) : 0
      }));

    // Proyectos activos para la card inferior
    const activeProjects = projects.filter(p => p.status === 'En curso').map(p => {
      let totalActualHours = 0;
      p.tasks?.forEach(t => {
        t.subtasks?.forEach(st => {
          totalActualHours += Number(st.actual_hours || 0);
        });
      });

      const hourlyRate = Number(p.rate?.hourly_rate || 0);
      const isTm = p.billing_mode === 'time_and_materials';
      const accruedAmount = isTm ? totalActualHours * hourlyRate : Number(p.budget_amount || 0);

      return {
        ...p,
        total_actual_hours: totalActualHours,
        accrued_amount: accruedAmount
      };
    });

    const urgentTasks = tasks.filter(t => t.status !== 'Completada').slice(0, 5);

    return {
      targetYear,
      targetMonth,
      monthKey,
      totalDevengado: currentMetrics.totalDevengado,
      accruedByBudget: currentMetrics.accruedByBudget,
      accruedByHours: currentMetrics.accruedByHours,
      accruedRecurringTotal: currentMetrics.accruedRecurringTotal,
      accruedPunctualTotal: currentMetrics.accruedPunctualTotal,
      hourlyValue: currentMetrics.hourlyValue,
      hourlyValueChangeReal,
      hourlyValueChangeNominal,
      currentMonthHours: currentMetrics.monthHours,
      currentMonthFixedHours: currentMetrics.monthFixedHours,
      currentMonthTmHours: currentMetrics.monthTmHours,
      currentMonthInternalHours: currentMetrics.monthInternalHours,
      currentMonthRecurringHours: currentMetrics.monthRecurringHours,
      currentMonthPunctualHours: currentMetrics.monthPunctualHours,
      currentWeekHours,
      hoursByProject,
      fixedCost,
      inflationRate,
      coveredPercent,
      isCovered,
      breakEvenDate,
      pipelinePotenciales,
      pipelinePropuestas,
      pipelineGanados,
      pipelinePausados,
      totalClientsCount: clients.length,
      evolutionHistory,
      activeProjectsCount: activeProjects.length,
      activeProjects,
      urgentTasks
    };
  },

  // Monthly Parameters (Fixed Costs and Inflation)
  async getMonthlyParameters() {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'monthly_parameters')
      .maybeSingle();
    if (error) throw error;
    return data?.value || {};
  },

  async saveMonthlyParameter(monthKey, { fixedCost, inflationRate }) {
    const current = await this.getMonthlyParameters();
    current[monthKey] = {
      fixed_cost: Number(fixedCost) || 0,
      inflation_rate: Number(inflationRate) || 0,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase
      .from('settings')
      .upsert({
        key: 'monthly_parameters',
        value: current,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    if (error) throw error;
    return data.value;
  },

  async deleteMonthlyParameter(monthKey) {
    const current = await this.getMonthlyParameters();
    delete current[monthKey];
    const { data, error } = await supabase
      .from('settings')
      .upsert({
        key: 'monthly_parameters',
        value: current,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    if (error) throw error;
    return data.value;
  },

  // Company Profile / Identidad Institucional (Equipo Alerce)
  async getCompanyProfile() {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'company_profile')
      .maybeSingle();
    if (error) throw error;
    return data?.value || {
      name: CONFIG.COMPANY_NAME || 'Equipo Alerce',
      email: CONFIG.COMPANY_EMAIL || 'contacto@equipoalerce.com',
      phone: CONFIG.COMPANY_PHONE || '+54 9 11 1234-5678'
    };
  },

  async saveCompanyProfile(profile) {
    const current = {
      name: profile.name?.trim() || 'Equipo Alerce',
      email: profile.email?.trim() || '',
      phone: profile.phone?.trim() || '',
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase
      .from('settings')
      .upsert({
        key: 'company_profile',
        value: current,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    if (error) throw error;
    return data.value;
  },

  // --- EMAIL MARKETING & CONTACTS MODULE ---

  // Contacts
  async getContacts(clientId = null) {
    let query = supabase
      .from('contacts')
      .select('*, client:clients(id, name, company, phase, groups, do_not_contact)')
      .order('is_primary', { ascending: false })
      .order('id', { ascending: true });

    if (clientId) {
      query = query.eq('client_id', clientId);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async createContact(contact) {
    const { data, error } = await supabase
      .from('contacts')
      .insert([contact])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateContact(id, updates) {
    const { data, error } = await supabase
      .from('contacts')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;

    // Keep client email and phone in sync if the updated contact is the primary contact
    if (data && data.is_primary && (updates.email !== undefined || updates.phone !== undefined)) {
      const clientSync = {};
      if (updates.email !== undefined) clientSync.email = updates.email || null;
      if (updates.phone !== undefined) clientSync.phone = updates.phone || null;
      if (Object.keys(clientSync).length > 0) {
        await supabase.from('clients').update({ ...clientSync, updated_at: new Date().toISOString() }).eq('id', data.client_id);
      }
    }

    return data;
  },

  async deleteContact(id) {
    // Check if the contact being deleted is primary and find its client_id
    const { data: contact } = await supabase
      .from('contacts')
      .select('id, client_id, is_primary')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('contacts')
      .delete()
      .eq('id', id);
    if (error) throw error;

    // If it was primary, promote another contact if available
    if (contact && contact.is_primary) {
      const { data: remaining } = await supabase
        .from('contacts')
        .select('id')
        .eq('client_id', contact.client_id)
        .order('id', { ascending: true })
        .limit(1);

      if (remaining && remaining.length > 0) {
        await this.setPrimaryContact(contact.client_id, remaining[0].id);
      }
    }
  },

  async setPrimaryContact(clientId, contactId) {
    // Demote all other contacts for this client
    await supabase
      .from('contacts')
      .update({ is_primary: false })
      .eq('client_id', clientId);

    // Promote target
    const { data, error } = await supabase
      .from('contacts')
      .update({ is_primary: true })
      .eq('id', contactId)
      .select()
      .single();
    if (error) throw error;

    // Sync primary contact's email & phone to clients table
    if (data) {
      await supabase.from('clients').update({
        email: data.email || null,
        phone: data.phone || null,
        updated_at: new Date().toISOString()
      }).eq('id', clientId);
    }

    return data;
  },

  // Email Accounts
  async getEmailAccounts() {
    const { data, error } = await supabase
      .from('email_accounts')
      .select('id, name, email_address, auth_type, daily_limit, sent_today, last_sent_date, status, last_error, created_at, updated_at')
      .order('id', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async createEmailAccount(account) {
    const { data, error } = await supabase
      .from('email_accounts')
      .insert([account])
      .select('id, name, email_address, auth_type, daily_limit, sent_today, status')
      .single();
    if (error) throw error;
    return data;
  },

  async updateEmailAccount(id, updates) {
    const { data, error } = await supabase
      .from('email_accounts')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select('id, name, email_address, auth_type, daily_limit, sent_today, status')
      .single();
    if (error) throw error;
    return data;
  },

  async deleteEmailAccount(id) {
    const { error } = await supabase
      .from('email_accounts')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  // Email Templates
  async getEmailTemplates() {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .order('id', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async createEmailTemplate(template) {
    const { data, error } = await supabase
      .from('email_templates')
      .insert([template])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateEmailTemplate(id, updates) {
    const { data, error } = await supabase
      .from('email_templates')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteEmailTemplate(id) {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  // Campaigns & Sequences
  async getCampaigns() {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          *,
          account:email_accounts(id, name, email_address),
          template:email_templates(id, name, subject),
          steps:campaign_steps(*),
          recipients:campaign_recipients(id, status, current_step)
        `)
        .order('id', { ascending: false });
      if (error) {
        console.error('Error fetching campaigns:', error);
        return [];
      }

      return (data || []).map(c => {
        const recs = c.recipients || [];
        const totalRecipients = recs.length;
        const sentOrActive = recs.filter(r => r.status === 'active' || r.status === 'completed' || r.status === 'replied').length;
        const replied = recs.filter(r => r.status === 'replied').length;
        return {
          ...c,
          total_recipients: totalRecipients,
          active_or_done: sentOrActive,
          replied_count: replied
        };
      });
    } catch (err) {
      console.error('Error in getCampaigns:', err);
      return [];
    }
  },

  async getCampaign(id) {
    const { data, error } = await supabase
      .from('campaigns')
      .select(`
        *,
        account:email_accounts(id, name, email_address, daily_limit, sent_today),
        template:email_templates(*),
        steps:campaign_steps(*, template:email_templates(*)),
        recipients:campaign_recipients(
          id, current_step, status, next_scheduled_at, exit_reason,
          contact:contacts(id, name, email, role, is_primary),
          client:clients(id, name, company, phase)
        )
      `)
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  async createCampaign({ campaign, steps = [], recipientContactIds = [] }) {
    // 1. Insert Campaign
    const { data: newCampaign, error: cError } = await supabase
      .from('campaigns')
      .insert([campaign])
      .select()
      .single();
    if (cError) throw cError;

    // 2. Insert Campaign Steps
    if (steps.length > 0) {
      const stepsToInsert = steps.map((s, idx) => ({
        campaign_id: newCampaign.id,
        step_order: idx,
        delay_days: Number(s.delay_days || 0),
        template_id: s.template_id || null,
        condition: s.condition || 'if_no_reply'
      }));
      const { error: sError } = await supabase.from('campaign_steps').insert(stepsToInsert);
      if (sError) console.error('Error inserting steps:', sError);
    }

    // 3. Enroll Recipients
    if (recipientContactIds.length > 0) {
      // Get contacts with their client_id
      const { data: contactsData } = await supabase
        .from('contacts')
        .select('id, client_id, do_not_contact, client:clients(do_not_contact)')
        .in('id', recipientContactIds);

      const validContacts = (contactsData || []).filter(c => !c.do_not_contact && !c.client?.do_not_contact);

      const recipientsToInsert = validContacts.map(c => ({
        campaign_id: newCampaign.id,
        contact_id: c.id,
        client_id: c.client_id,
        current_step: 0,
        status: 'queued',
        next_scheduled_at: campaign.start_at || new Date().toISOString()
      }));

      if (recipientsToInsert.length > 0) {
        const { error: rError } = await supabase.from('campaign_recipients').insert(recipientsToInsert);
        if (rError) console.error('Error inserting recipients:', rError);
      }
    }

    return newCampaign;
  },

  async updateCampaign(id, updates) {
    const { data, error } = await supabase
      .from('campaigns')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteCampaign(id) {
    const { error } = await supabase.from('campaigns').delete().eq('id', id);
    if (error) throw error;
  },

  // Direct Reactivation / Single Email Sending
  async sendDirectEmail({ clientId, contactId, accountId, templateId, customSubject, customBody }) {
    // Fetch contact, client, account, and template
    const [contactRes, clientRes, accountRes, templateRes] = await Promise.all([
      supabase.from('contacts').select('*').eq('id', contactId).single(),
      supabase.from('clients').select('*').eq('id', clientId).single(),
      supabase.from('email_accounts').select('*').eq('id', accountId).single(),
      templateId ? supabase.from('email_templates').select('*').eq('id', templateId).single() : Promise.resolve({ data: null })
    ]);

    const contact = contactRes.data;
    const client = clientRes.data;
    const account = accountRes.data;
    const template = templateRes.data;

    if (!contact || !client || !account) {
      throw new Error('Información insuficiente para enviar el correo.');
    }

    if (contact.do_not_contact || client.do_not_contact) {
      throw new Error('Este contacto o cliente está marcado como "No contactar".');
    }

    const firstName = contact.first_name || contact.name.split(' ')[0] || 'Hola';
    const companyName = client.company && client.company !== '-' ? client.company : client.name;
    const senderName = account.name || 'Equipo Alerce';

    let subject = customSubject || template?.subject || 'Contacto desde Equipo Alerce';
    subject = subject
      .replace(/\{\{contact\.first_name\}\}/gi, firstName)
      .replace(/\{\{contact\.name\}\}/gi, contact.name)
      .replace(/\{\{company\.name\}\}/gi, companyName)
      .replace(/\{\{sender\.name\}\}/gi, senderName);

    let htmlContent = customBody || template?.body_html || template?.body_markdown || '';
    htmlContent = htmlContent
      .replace(/\{\{contact\.first_name\}\}/gi, firstName)
      .replace(/\{\{contact\.name\}\}/gi, contact.name)
      .replace(/\{\{company\.name\}\}/gi, companyName)
      .replace(/\{\{sender\.name\}\}/gi, senderName);

    const trackingToken = crypto.randomUUID();
    const now = new Date().toISOString();

    // Call Supabase Edge Function send-campaign-emails
    const { data: fnData, error: fnError } = await supabase.functions.invoke('send-campaign-emails', {
      body: {
        recipient_id: null,
        direct: true,
        contact_id: contact.id,
        client_id: client.id,
        account_id: account.id,
        subject,
        html_content: htmlContent,
        template_id: template?.id || null,
        ctas: Array.isArray(template?.ctas) ? template.ctas : []
      }
    });

    if (fnError) {
      let msg = fnError.message;
      try {
        if (fnError.context && typeof fnError.context.json === 'function') {
          const errorBody = await fnError.context.json();
          if (errorBody?.error) msg = errorBody.error;
        }
      } catch (_) {}
      throw new Error(msg);
    }
    if (fnData && !fnData.success) {
      throw new Error(fnData.error || 'No se pudo enviar el correo.');
    }

    return { success: true, subject, messageId: fnData?.message_id };
  },

  // Manual Reply Registration
  async logManualReply({ clientId, contactId, recipientId = null, channel = 'manual', notes = '', newStage = null }) {
    // Invoke check-replies edge function or perform direct update
    try {
      const { data, error } = await supabase.functions.invoke('check-replies', {
        body: { manual: true, client_id: clientId, contact_id: contactId, recipient_id: recipientId, channel, notes, new_stage: newStage }
      });
      if (!error && data?.success) return data;
    } catch (_) {
      // Direct fallback
    }

    const now = new Date().toISOString();

    // 1. Cancel queued/active sequence steps
    if (recipientId) {
      await supabase.from('campaign_recipients').update({ status: 'replied', exit_reason: `Respuesta vía ${channel}`, next_scheduled_at: null }).eq('id', recipientId);
    } else if (contactId) {
      await supabase.from('campaign_recipients').update({ status: 'replied', exit_reason: `Respuesta vía ${channel}`, next_scheduled_at: null }).eq('contact_id', contactId).in('status', ['queued', 'active']);
    }

    // 2. Update active logs
    let logQuery = supabase.from('email_logs').update({ replied_at: now, reply_channel: channel, reply_notes: notes || null, thread_active: false, status: 'replied' });
    if (contactId) logQuery = logQuery.eq('contact_id', contactId).is('replied_at', null);
    else if (clientId) logQuery = logQuery.eq('client_id', clientId).is('replied_at', null);
    await logQuery;

    // 3. Activity
    if (clientId) {
      await supabase.from('activities').insert([{
        entity_type: 'client',
        entity_id: clientId,
        type: 'Email Marketing',
        title: `Respuesta recibida (${channel})`,
        details: notes || 'El cliente respondió a la comunicación. Secuencias automáticas detenidas.',
        occurred_at: now,
        created_at: now
      }]);

      if (newStage) {
        await supabase.from('opportunities').update({ stage: newStage, updated_at: now }).eq('client_id', clientId);
      }
    }

    return { success: true };
  },

  // Individual Email Logs with Sorting and Filtering
  async getEmailLogs({ campaignId = null, clientId = null, status = 'all', limit = 100 } = {}) {
    try {
      let query = supabase
        .from('email_logs')
        .select(`
          *,
          contact:contacts(id, name, email, role),
          client:clients(id, name, company),
          account:email_accounts(id, name, email_address),
          campaign:campaigns(id, name),
          template:email_templates(id, name, subject),
          clicks:email_cta_clicks(id, cta_id, cta_text, destination_url, clicked_at)
        `)
        .order('sent_at', { ascending: false })
        .limit(limit);

      if (campaignId) query = query.eq('campaign_id', campaignId);
      if (clientId) query = query.eq('client_id', clientId);
      if (status && status !== 'all') query = query.eq('status', status);

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching email logs:', error);
        return [];
      }
      return data || [];
    } catch (err) {
      console.error('Error in getEmailLogs:', err);
      return [];
    }
  },

  // Update an email log entry (e.g. reply notes)
  async updateEmailLog(id, updates) {
    const { data, error } = await supabase.from('email_logs').update(updates).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },

  // Aggregated Marketing Stats & Funnel
  async getEmailMarketingStats() {
    try {
      const [logsRes, clicksRes, campaignsRes, oppsRes, templatesRes] = await Promise.all([
        supabase.from('email_logs').select('id, client_id, campaign_id, template_id, status, opened_at, replied_at, sent_at'),
        supabase.from('email_cta_clicks').select('id, cta_id, cta_text, destination_url, campaign_id'),
        supabase.from('campaigns').select('id, name, status, template_id'),
        supabase.from('opportunities').select('id, client_id, stage, estimated_amount'),
        supabase.from('email_templates').select('id, name, subject')
      ]);

      const logs = logsRes.data || [];
      const clicks = clicksRes.data || [];
      const campaigns = campaignsRes.data || [];
      const opps = oppsRes.data || [];
      const templates = templatesRes.data || [];

      const totalSent = logs.length;
      const totalOpened = logs.filter(l => l.opened_at !== null).length;
      const totalClicked = logs.filter(l => l.status === 'clicked' || clicks.some(c => c.id)).length;
      const totalReplied = logs.filter(l => l.replied_at !== null).length;

      const openRate = totalSent > 0 ? ((totalOpened / totalSent) * 100).toFixed(1) : '0.0';
      const clickRate = totalSent > 0 ? ((clicks.length / totalSent) * 100).toFixed(1) : '0.0';
      const replyRate = totalSent > 0 ? ((totalReplied / totalSent) * 100).toFixed(1) : '0.0';

      // CTA stats aggregation
      const ctaMap = {};
      clicks.forEach(c => {
        const label = c.cta_text || c.cta_id || 'CTA';
        ctaMap[label] = (ctaMap[label] || 0) + 1;
      });

      // Template stats aggregation (Rendimiento por Plantilla)
      const templateMap = new Map();
      templates.forEach(t => {
        templateMap.set(t.id, {
          id: t.id,
          name: t.name,
          subject: t.subject,
          sent: 0,
          opened: 0,
          clicked: 0,
          replied: 0
        });
      });

      const campaignTemplateMap = new Map();
      campaigns.forEach(c => {
        if (c.template_id) campaignTemplateMap.set(c.id, c.template_id);
      });

      logs.forEach(l => {
        let tId = l.template_id;
        if (!tId && l.campaign_id) tId = campaignTemplateMap.get(l.campaign_id);
        
        let tStats = tId ? templateMap.get(tId) : null;
        if (!tStats && tId) {
          tStats = { id: tId, name: `Plantilla #${tId}`, subject: '', sent: 0, opened: 0, clicked: 0, replied: 0 };
          templateMap.set(tId, tStats);
        }
        if (tStats) {
          tStats.sent++;
          if (l.opened_at) tStats.opened++;
          if (l.status === 'clicked' || l.status === 'replied') tStats.clicked++;
          if (l.replied_at) tStats.replied++;
        }
      });

      const templateBreakdown = Array.from(templateMap.values())
        .map(t => ({
          ...t,
          openRate: t.sent > 0 ? ((t.opened / t.sent) * 100).toFixed(1) : '0.0',
          clickRate: t.sent > 0 ? ((t.clicked / t.sent) * 100).toFixed(1) : '0.0',
          replyRate: t.sent > 0 ? ((t.replied / t.sent) * 100).toFixed(1) : '0.0'
        }))
        .sort((a, b) => b.sent - a.sent);

      // Funnel: 5 coherent marketing steps
      // 1. Enviados -> 2. Abiertos -> 3. Clicks -> 4. Respuestas -> 5. Clientes Ganados
      const clientsWithEmails = new Set(logs.map(l => l.client_id).filter(Boolean));
      const relevantOpps = totalSent > 0 ? opps.filter(o => clientsWithEmails.has(o.client_id)) : [];

      const diagnósticos = relevantOpps.filter(o => o.stage === 'Propuesta en curso').length;
      const propuestas = relevantOpps.filter(o => o.stage === 'Propuesta enviada').length;
      const clientesGanados = relevantOpps.filter(o => o.stage === 'Ganado' || o.stage === 'Cerrado').length;

      return {
        totalSent,
        totalOpened,
        totalClicked: clicks.length,
        totalReplied,
        openRate,
        clickRate,
        replyRate,
        activeCampaignsCount: campaigns.filter(c => c.status === 'running').length,
        ctaBreakdown: Object.entries(ctaMap).map(([text, count]) => ({ text, count })),
        templateBreakdown,
        funnel: {
          sent: totalSent,
          opened: totalOpened,
          clicked: clicks.length,
          replies: totalReplied,
          diagnostics: diagnósticos,
          proposals: propuestas,
          won: clientesGanados
        }
      };
    } catch (err) {
      console.error('Error fetching email marketing stats:', err);
      return {
        totalSent: 0,
        totalOpened: 0,
        totalClicked: 0,
        totalReplied: 0,
        openRate: '0.0',
        clickRate: '0.0',
        replyRate: '0.0',
        activeCampaignsCount: 0,
        ctaBreakdown: [],
        templateBreakdown: [],
        funnel: { sent: 0, opened: 0, clicked: 0, replies: 0, diagnostics: 0, proposals: 0, won: 0 }
      };
    }
  },

  // Trigger automated sequence batch
  async runEmailAutomationBatch() {
    const { data, error } = await supabase.functions.invoke('send-campaign-emails', { body: {} });
    if (error) {
      let msg = error.message;
      try {
        if (error.context && typeof error.context.json === 'function') {
          const errorBody = await error.context.json();
          if (errorBody?.error) msg = errorBody.error;
        }
      } catch (_) {}
      throw new Error(msg);
    }
    if (data && !data.success) {
      throw new Error(data.error || 'Error procesando la cola de envíos.');
    }
    if (data && data.errors && data.errors.length > 0) {
      throw new Error(data.errors[0].error || 'Ocurrió un error en uno de los envíos.');
    }
    return data;
  },

  // Check email replies (via Edge function / inbox monitor)
  async checkEmailReplies() {
    try {
      const { data, error } = await supabase.functions.invoke('check-replies', { body: {} });
      if (error) {
        let msg = error.message;
        try {
          if (error.context && typeof error.context.json === 'function') {
            const errorBody = await error.context.json();
            if (errorBody?.error) msg = errorBody.error;
          }
        } catch (_) {}
        throw new Error(msg);
      }
      return data || { success: true, message: 'Buzones sincronizados.' };
    } catch (err) {
      console.warn('checkEmailReplies error:', err);
      // Non-blocking fallback response
      return { success: true, replies_detected: 0, message: err.message };
    }
  },

  // Supabase Storage: Recursos
  async listResources() {
    try {
      const { data, error } = await supabase.storage.from('recursos').list('', {
        limit: 100,
        sortBy: { column: 'created_at', order: 'desc' }
      });
      if (error) throw error;
      return (data || [])
        .filter(item => item.name && item.name !== '.emptyFolderPlaceholder')
        .map(file => {
          const { data: publicUrlData } = supabase.storage.from('recursos').getPublicUrl(file.name);
          return {
            name: file.name,
            id: file.id,
            created_at: file.created_at,
            updated_at: file.updated_at,
            size: file.metadata?.size || 0,
            mimetype: file.metadata?.mimetype || '',
            publicUrl: publicUrlData?.publicUrl || ''
          };
        });
    } catch (err) {
      console.error('Error fetching recursos from storage:', err);
      throw err;
    }
  },

  async uploadResource(file, customPrefix = '') {
    const ext = file.name.split('.').pop().toLowerCase();
    const rawBaseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
    const cleanBase = (customPrefix || rawBaseName)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .toLowerCase();
    const finalName = `${cleanBase}_${Date.now()}.${ext}`;

    const { data, error } = await supabase.storage.from('recursos').upload(finalName, file, {
      cacheControl: '3600',
      upsert: true
    });
    if (error) throw error;

    const { data: publicUrlData } = supabase.storage.from('recursos').getPublicUrl(finalName);
    return {
      name: finalName,
      publicUrl: publicUrlData?.publicUrl || '',
      ...data
    };
  },

  async deleteResource(fileName) {
    const { data, error } = await supabase.storage.from('recursos').remove([fileName]);
    if (error) throw error;
    return data;
  },

  // --- WEBHOOK LANDING & INTEGRACIONES ---
  async getWebhookConfig() {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'webhook_landing_config')
        .maybeSingle();
      if (error) throw error;
      return data?.value || {
        api_key: 'alerce-landing-diag-2026',
        template_id: null,
        account_id: null,
        send_email_enabled: false,
        admin_email_recipient: 'martin.batarev@gmail.com',
        admin_template_id: null,
        notify_admin_enabled: true,
        updated_at: null
      };
    } catch (err) {
      console.warn('Could not read webhook config:', err.message);
      return {
        api_key: 'alerce-landing-diag-2026',
        template_id: null,
        account_id: null,
        send_email_enabled: false,
        admin_email_recipient: 'martin.batarev@gmail.com',
        admin_template_id: null,
        notify_admin_enabled: true,
        updated_at: null
      };
    }
  },

  async saveWebhookConfig(config) {
    const value = {
      api_key: config.api_key || 'alerce-landing-diag-2026',
      template_id: config.template_id ? Number(config.template_id) : null,
      account_id: config.account_id ? Number(config.account_id) : null,
      send_email_enabled: Boolean(config.send_email_enabled),
      admin_email_recipient: (config.admin_email_recipient || 'martin.batarev@gmail.com').trim(),
      admin_template_id: config.admin_template_id ? Number(config.admin_template_id) : null,
      notify_admin_enabled: config.notify_admin_enabled !== false,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase
      .from('settings')
      .upsert({
        key: 'webhook_landing_config',
        value,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    if (error) throw error;
    return data.value;
  },

  // --- WEBHOOK LEADS GENÉRICOS (WAPATIENDA / SAAS) ---
  async getWebhookLeadsConfig() {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'webhook_leads_config')
        .maybeSingle();
      if (error) throw error;
      if (data?.value) return data.value;
    } catch (err) {
      console.warn('Could not read webhook leads config:', err.message);
    }
    return {
      api_key: 'alerce-landing-diag-2026',
      account_id: null,
      send_lead_email_enabled: false,
      lead_template_id: null,
      notify_admin_enabled: true,
      admin_email_recipient: 'martin.batarev@gmail.com',
      admin_template_id: null,
      updated_at: null
    };
  },

  async saveWebhookLeadsConfig(config) {
    const value = {
      api_key: config.api_key || 'alerce-landing-diag-2026',
      account_id: config.account_id ? Number(config.account_id) : null,
      send_lead_email_enabled: Boolean(config.send_lead_email_enabled),
      lead_template_id: config.lead_template_id ? Number(config.lead_template_id) : null,
      notify_admin_enabled: config.notify_admin_enabled !== false,
      admin_email_recipient: (config.admin_email_recipient || 'martin.batarev@gmail.com').trim(),
      admin_template_id: config.admin_template_id ? Number(config.admin_template_id) : null,
      updated_at: new Date().toISOString()
    };
    const { data, error } = await supabase
      .from('settings')
      .upsert({
        key: 'webhook_leads_config',
        value,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    if (error) throw error;
    return data.value;
  },

  // Batch import de clientes desde CSV con creación de Contacto y Oportunidad
  async importClientsBatch(validRows) {
    const results = {
      total: validRows.length,
      imported: 0,
      errors: []
    };

    const nowIso = new Date().toISOString();

    for (const item of validRows) {
      const clientData = {
        name: item.clientData.name.slice(0, 255),
        company: (item.clientData.company || item.clientData.name).slice(0, 255),
        email: item.clientData.email ? item.clientData.email.slice(0, 255) : null,
        phone: item.clientData.phone ? item.clientData.phone.slice(0, 50) : null,
        phase: item.clientData.phase || 'Cliente potencial',
        channel: item.clientData.channel || 'Directo',
        country: item.clientData.country || 'Argentina',
        groups: Array.isArray(item.clientData.groups) ? item.clientData.groups : ['prospecto'],
        socials: item.clientData.socials ? item.clientData.socials.slice(0, 255) : null,
        notes: item.clientData.notes || null,
        do_not_contact: Boolean(item.clientData.do_not_contact),
        status: 'Activo',
        created_at: nowIso,
        updated_at: nowIso
      };

      try {
        const { data: newClient, error: clientErr } = await supabase
          .from('clients')
          .insert([clientData])
          .select()
          .single();

        if (clientErr) throw clientErr;

        // Inserción en contacts (Contacto Principal)
        await supabase.from('contacts').insert([{
          client_id: newClient.id,
          name: newClient.name,
          role: 'Contacto Principal',
          email: newClient.email || null,
          phone: newClient.phone || null,
          is_primary: true,
          do_not_contact: newClient.do_not_contact,
          created_at: nowIso,
          updated_at: nowIso
        }]);

        // Inserción en opportunities (Pipeline comercial)
        await supabase.from('opportunities').insert([{
          client_id: newClient.id,
          title: `Oportunidad - ${newClient.name}`.slice(0, 255),
          stage: newClient.phase,
          estimated_amount: 0,
          currency: 'ARS',
          probability: 50,
          notes: newClient.notes || 'Importado vía archivo CSV',
          created_at: nowIso,
          updated_at: nowIso
        }]);

        // Inserción en activities (opcional)
        await supabase.from('activities').insert([{
          entity_type: 'client',
          entity_id: newClient.id,
          title: 'Cliente importado vía CSV',
          description: `Alta masiva en Pipeline Comercial. Canal: ${newClient.channel}.`,
          created_at: nowIso
        }]).catch(() => {});

        results.imported++;
      } catch (err) {
        console.error(`Error importando fila ${item._rowNumber}:`, err);
        results.errors.push({
          row: item._rowNumber,
          name: item.clientData.name,
          message: err.message
        });
      }
    }

    return results;
  },

  // Obtener diagnóstico de lead (desde lead_diagnostics o desde notes)
  async getLeadDiagnosticByClientId(clientId, clientNotes = '') {
    try {
      const { data, error } = await supabase
        .from('lead_diagnostics')
        .select('*')
        .eq('client_id', clientId)
        .order('id', { ascending: false })
        .limit(1);

      if (!error && data && data.length > 0) {
        return data[0];
      }
    } catch (_) {
      // Si la tabla lead_diagnostics aún no existe en Supabase, continúa al fallback
    }

    // Fallback: verificar si las respuestas vienen guardadas estructuradas en clientNotes
    if (clientNotes && clientNotes.includes('[DIAGNÓSTICO WEB:')) {
      try {
        const jsonMatch = clientNotes.match(/<!-- DIAGNOSTIC_DATA: (.*) -->/);
        if (jsonMatch && jsonMatch[1]) {
          return JSON.parse(jsonMatch[1]);
        }
      } catch (_) {}
    }

    return null;
  }
};


